// UserContext.js
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

// Firebase
import { getIdTokenResult, onAuthStateChanged, onIdTokenChanged } from 'firebase/auth';
import { auth } from 'firebaseConfig';
import {
  ensureWebPushSubscriptionQuietly,
  startForegroundPushListener,
  stopForegroundPushListener,
} from 'services/push/WebPushService';

// Security
import { hasSuperUserClaim } from 'security/superUser';

// Utilities
import { ProfileTab } from 'utilities/Users';

// Context
export const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }

  return context;
};

export const UserProvider = ({ children }) => {

  /**
   * The user object contains current user and user preference information.
   */
  const [user, setUser] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [authClaims, setAuthClaims] = useState({});
  const [isAuthClaimsLoading, setIsAuthClaimsLoading] = useState(true);
  const [isEmailVerifiedFromToken, setIsEmailVerifiedFromToken] = useState(false);

  /**
   * User currently being viewed by the current user.
   */
  const [displayedUser, setDisplayedUser] = useState(null);

  /**
   * This user-specific state variables are preloaded when the 
   * user is loaded, tied to the user, and updated using real-time updates.
   */
  const [userEngagements, setUserEngagements] = useState([]);
  // Bounded, unread-only feed behind badge counts; see usePreloadUserNotifications.
  const [userUnreadNotifications, setUserUnreadNotifications] = useState([]);
  const [isUserSettingsLoading, setIsUserSettingsLoading] = useState(true);
  const [userSettings, setUserSettings] = useState(null);
  const [userStorageUsage, setUserStorageUsage] = useState(0); // Bytes
  const [userStreamingUsage, setUserStreamingUsage] = useState(0); // Seconds
  const [activeProfileTab, setActiveProfileTab] = useState(ProfileTab.NODES);

  /**
   * Quotas
   */
  const [userStorageQuotaReached, setUserStorageQuotaReached] = useState(true);
  const [userStreamingQuotaReached, setUserStreamingQuotaReached] = useState(true);

  useEffect(() => {
    const unsubscribeAuthState = onAuthStateChanged(auth, (nextAuthUser) => {
      setAuthUser(nextAuthUser || null);
    });

    return () => {
      unsubscribeAuthState();
    };
  }, []);

  // Refresh FCM web push token when already permitted (no permission prompt),
  // and again when the tab becomes visible (recovers after NotRegistered disables).
  useEffect(() => {
    if (!authUser?.uid) return undefined;

    const refresh = () => {
      ensureWebPushSubscriptionQuietly().catch(() => {});
    };
    refresh();
    startForegroundPushListener().catch(() => {});

    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refresh);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refresh);
      stopForegroundPushListener();
    };
  }, [authUser?.uid]);

  useEffect(() => {
    let isMounted = true;

    const unsubscribe = onIdTokenChanged(auth, async (nextAuthUser) => {
      if (!isMounted) return;

      if (!nextAuthUser) {
        setAuthClaims({});
        setIsEmailVerifiedFromToken(false);
        setIsAuthClaimsLoading(false);
        return;
      }

      setIsAuthClaimsLoading(true);

      try {
        const tokenResult = await getIdTokenResult(nextAuthUser);
        if (!isMounted) return;

        const claims = tokenResult?.claims && typeof tokenResult.claims === 'object'
          ? tokenResult.claims
          : {};

        setAuthClaims(claims);
        setIsEmailVerifiedFromToken(claims.email_verified === true);
      } catch (_error) {
        if (!isMounted) return;
        setAuthClaims({});
        setIsEmailVerifiedFromToken(nextAuthUser.emailVerified === true);
      } finally {
        if (isMounted) {
          setIsAuthClaimsLoading(false);
        }
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  const isSuperUser = hasSuperUserClaim(authClaims);

  const isEmailVerified = useMemo(() => {
    if (isSuperUser) return true;
    return isEmailVerifiedFromToken || authUser?.emailVerified === true;
  }, [authUser, isEmailVerifiedFromToken, isSuperUser]);

  const value = useMemo(() => ({

    // User
    user, setUser,
    authUser,
    isAuthLoading, setIsAuthLoading,
    authClaims,
    isAuthClaimsLoading,
    isSuperUser,
    isEmailVerified,

    // User Being Viewed
    displayedUser, setDisplayedUser,

    // User Preloads
    userEngagements, setUserEngagements,
    userUnreadNotifications, setUserUnreadNotifications,
    isUserSettingsLoading, setIsUserSettingsLoading,
    userSettings, setUserSettings,
    userStorageUsage, setUserStorageUsage,
    userStreamingUsage, setUserStreamingUsage,

    // Quotas
    userStorageQuotaReached, setUserStorageQuotaReached,
    userStreamingQuotaReached, setUserStreamingQuotaReached,

    // Profile Tab
    activeProfileTab, setActiveProfileTab,

  }), [
    activeProfileTab,
    authClaims,
    authUser,
    isAuthLoading,
    isAuthClaimsLoading,
    isEmailVerified,
    isSuperUser,
    displayedUser,
    isUserSettingsLoading,
    user,
    userEngagements,
    userUnreadNotifications,
    userSettings,
    userStorageQuotaReached,
    userStorageUsage,
    userStreamingQuotaReached,
    userStreamingUsage,
  ]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
